import React from "react";
import { motion } from "framer-motion";

const HelpDocument = () => {
  return (
    <div className="min-h-screen bg-white py-12 px-6 md:px-10 text-[15px] leading-relaxed">
      <motion.div className="mx-auto max-w-8xl mb-12 border-b border-violet-200 pb-4 max-md:pb-0 flex justify-between items-center"
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <div>
          <h1 className="text-3xl font-bold mb-2 max-md:mt-5">User Documentation</h1>
          <p className="text-sm text-violet-600">
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-gray-600">Need assistance?</span>
          <a
            href="https://selfsync.ai/contact-us"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-gradient text-white font-medium py-2 px-4 rounded-md transition-colors duration-200"
          >
            Contact Us
          </a>
        </div>
      </motion.div>

      <div className="flex flex-col lg:flex-row gap-10 max-w-8xl mx-auto">
        {/* Main Content */}
        <motion.div className="flex-1 space-y-12"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <section id="introduction">
            <h2 className="text-xl font-semibold mb-4">1. Introduction</h2>
            <p className="text-gray-700 mb-3">
              This document provides a comprehensive and detailed explanation of the various features, tools, and resources available within the SelfSync.ai platform. It is designed to assist users in understanding the platform's capabilities and to help them navigate and utilize each section effectively to meet their professional or organizational goals.
            </p>
            <p className="text-gray-700">
              If you require additional support, guidance, or clarification regarding any aspect of the platform, we encourage you to reach out to our support team by emailing us directly at <a href="mailto:support@selfsync.ai" className="text-violet-600 underline">support@selfsync.ai</a>. Our dedicated team is always here to assist you.
            </p>
          </section>

          <section id="dashboard">
            <h2 className="text-xl font-semibold mb-4">2. Dashboard Overview</h2>
            <ul className="list-disc pl-6 text-gray-700 space-y-4">
              <li><strong>Total Assessment Count:</strong> Shows the number of assessments you've created on the platform.</li>
              <li><strong>Completed & Incomplete Assessments:</strong> Tracks which assessments have been completed and which are pending.</li>
              <li><strong>Open & Closed Positions:</strong> View all job positions you've posted and their current status.</li>
              <li><strong>Site Admin & Staff Users:</strong> Displays users added by the account owner or you, along with their roles.</li>
              <li><strong>Credit Info:</strong> New users get 2 free credits. Each assessment deducts 1 credit.</li>
              <li><strong>Transaction History:</strong> View all payment activities and credit usage logs.</li>
              <li><strong>System Notifications:</strong> Stay informed with feature updates and important alerts.</li>
              <li><strong>Performance Summaries:</strong> Quickly view your top assessment and hiring activity.</li>
            </ul>
          </section>

          <section id="assessments">
            <h2 className="text-xl font-semibold mb-4">3. Assessment Types</h2>
            <ul className="list-disc pl-6 text-gray-700 space-y-4">
              <li><strong>New Joining:</strong> Ideal for onboarding new hires. Send assessments individually or in bulk via CSV or vendor.</li>
              <li><strong>Existing Employee:</strong> Designed for internal evaluations. Assessees select a Reporting Manager before submitting.</li>
              <li><strong>CSV Upload:</strong> Bulk upload candidate emails using a standard template.</li>
              <li><strong>Assessment Duration:</strong> 60 questions, estimated time 45 minutes.</li>
              <li><strong>Email Templates:</strong> Customize the message sent with each assessment.</li>
            </ul>
          </section>

          <section id="results">
            <h2 className="text-xl font-semibold mb-4">4. Assessment Results</h2>
            <ul className="list-disc pl-6 text-gray-700 space-y-4">
              <li><strong>Report Overview:</strong> View results from your assessments or those shared by vendors.</li>
              <li><strong>Status Info:</strong> Check whether the report is generated or pending.</li>
              <li><strong>View Button:</strong> Opens a detailed report with insights and recommendations.</li>
              <li><strong>Share Option:</strong> Share instantly via WhatsApp, email, or by copying a link.</li>
            </ul>
          </section>

          {/* <section id="open-position">
            <h2 className="text-xl font-semibold mb-4">5. Hire Through Vendor</h2>
            <ul className="list-disc pl-6 text-gray-700 space-y-4">
              <li><strong>Create Job Openings:</strong> Enter job title and description.</li>
              <li><strong>Vendor Email:</strong> Add vendor email.</li>
              <li><strong>Tags & Expiry:</strong> Organize and manage listings efficiently.</li>
            </ul>
          </section> */}

          {/* <section id="view-position">
            <h2 className="text-xl font-semibold mb-4">6. View Open Positions</h2>
            <ul className="list-disc pl-6 text-gray-700 space-y-4">
              <li><strong>Data</strong> Lists your company's job postings.</li>
              <li><strong>Filter & Search:</strong> Sort and find positions easily.</li>
            </ul>
          </section> */}

          <section id="roles">
            <h2 className="text-xl font-semibold mb-4">5. Role Assignment</h2>
            <ul className="list-disc pl-6 text-gray-700 space-y-4">
              <li><strong>Add Site Admins & Site Manager:</strong> Enter name and email to assign roles.</li>
              <li><strong>Remove Roles:</strong> Click the 'Remove' button to revoke.</li>
              <li><strong>Reporting Managers:</strong> These users can view assessment results of employees assigned to them.</li>
              <li><strong>Hierarchy View:</strong> Visual layout of all roles and relationships.</li>
            </ul>
          </section>

          <section id="email-templates">
            <h2 className="text-xl font-semibold mb-4">6. Email Templates</h2>
            <ul className="list-disc pl-6 text-gray-700 space-y-4">
              <li><strong>Select Template:</strong> Choose from default or create new templates.</li>
              <li><strong>Custom Fields:</strong> Use placeholders like <code>{ }{ }candidate name{ }{ }</code> and <code>{ }{ }assessment date{ }{ }</code>.</li>
            </ul>
          </section>

          <section id="profile">
            <h2 className="text-xl font-semibold mb-4">7. My Profile</h2>
            <ul className="list-disc pl-6 text-gray-700 space-y-4">
              <li><strong>Update Details:</strong> Keep your personal and company info updated.</li>
              <li><strong>Subscriptions:</strong> View and manage your current plan.</li>
              <li><strong>Notifications:</strong> Add emails to receive platform alerts.</li>
            </ul>
          </section>

          <section id="credits">
            <h2 className="text-xl font-semibold mb-4">8. Credits & Referral</h2>
            <ul className="list-disc pl-6 text-gray-700 space-y-4">
              <li><strong>Free Credits:</strong> New users receive 2 free credits.</li>
              <li><strong>Referral Program:</strong> Share on social, tag us, and email proof to earn more credits.</li>
              <li><strong>Credit Usage:</strong> Each assessment deducts 1 credit.</li>
            </ul>
          </section>

          <section id="support">
            <h2 className="text-xl font-semibold mb-4">9. Help & Support</h2>
            <ul className="list-disc pl-6 text-gray-700 space-y-4">
              <li><strong>Help Center:</strong> Browse articles and FAQs anytime.</li>
              <li><strong>Talk to Our Team:</strong> Send us a message—we reply within 12 hours.</li>
              <li><strong>Community Forum:</strong> Collaborate and share with other users.</li>
            </ul>
          </section>
        </motion.div>

        {/* Sidebar Navigation */}
        <motion.div className="w-full lg:w-64 sticky top-24 h-fit bg-white border-l border-gray-200 pl-4"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <h3 className="text-lg font-semibold mb-4">Quick Navigation</h3>
          <ul className="text-sm text-violet-700 space-y-2">
            <li><a href="#introduction" className="hover:underline">1. Introduction</a></li>
            <li><a href="#dashboard" className="hover:underline">2. Dashboard Overview</a></li>
            <li><a href="#assessments" className="hover:underline">3. Assessment Types</a></li>
            <li><a href="#results" className="hover:underline">4. Assessment Results</a></li>
            {/* <li><a href="#open-position" className="hover:underline">5. Hire Through Vendor</a></li>
            <li><a href="#view-position" className="hover:underline">6. View Open Positions</a></li> */}
            <li><a href="#roles" className="hover:underline">5. Role Assignment</a></li>
            <li><a href="#email-templates" className="hover:underline">6. Email Templates</a></li>
            <li><a href="#profile" className="hover:underline">7. My Profile</a></li>
            <li><a href="#credits" className="hover:underline">8. Credits & Referral</a></li>
            <li><a href="#support" className="hover:underline">9. Help & Support</a></li>
          </ul>
        </motion.div>
      </div>
    </div>
  );
};

export default HelpDocument;